import { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, r4, spacing } from '@ardoq/design-tokens';
import { header4, text2 } from '@ardoq/typography';
import { focusableCSS } from '@ardoq/style-helpers';
import { FIELD_SELECTOR } from '@ardoq/forms';
import {
  BUTTON_GROUP_SELECTOR,
  BUTTON_SELECTOR,
  ButtonType,
  IconButton,
  IconButtonProps,
} from '@ardoq/button';
import {
  AnimatedChevron,
  Icon,
  ICON_SELECTOR,
  IconName,
  IconSize,
} from '@ardoq/icons';
import { SIDEBAR_MENU_MENU_SELECTOR } from './SidebarMenuMenu';
import { SIDEBAR_MENU_SUBSECTION_HEADER_SELECTOR } from './SidebarMenuSubsectionHeader';
import { PopoverContentType, WithPopover } from '@ardoq/popovers';
import { Stack, SpaceVariant, FlexBox } from '@ardoq/layout';
import { TAB_LIST_SELECTOR } from '@ardoq/tabs';
import { SIDEBAR_MENU_MENU_ITEM_SELECTOR } from './SidebarMenuItem';

const sidebarMenuSectionTitleCSS = `
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${colors.textSubtle};
  ${header4};
  padding: ${spacing.s8};
  margin: 0 ${spacing.s8};
  background: ${colors.surfaceDefault};
  border: none;
  border-radius: ${r4};

  ${ICON_SELECTOR} {
    color: ${colors.icon};
  }
`;

const CollapsableSidebarMenuSectionTitle = styled.button`
  ${sidebarMenuSectionTitleCSS};
  ${focusableCSS};

  ${({ onClick, disabled }) =>
    onClick &&
    !disabled &&
    css`
      cursor: pointer;
      &:hover {
        color: ${colors.textModerate};
        background: ${colors.surfaceHover};
        ${ICON_SELECTOR} {
          color: ${colors.icon};
        }
      }
    `}
`;

const UncollapsableSidebarMenuSectionTitle = styled.div`
  ${sidebarMenuSectionTitleCSS};
`;

export const COLLAPSIBLE_SIDEBAR_MENU_SECTION_TITLE_SELECTOR =
  CollapsableSidebarMenuSectionTitle.toString();

export const UNCOLLAPSABLE_SIDEBAR_MENU_SECTION_TITLE_SELECTOR =
  UncollapsableSidebarMenuSectionTitle.toString();

const SidebarMenuSectionBody = styled(Stack)`
  background: ${colors.surfaceDefault};

  & > ${FIELD_SELECTOR}, & > ${SIDEBAR_MENU_SUBSECTION_HEADER_SELECTOR} {
    margin: 0 ${spacing.s16};
  }

  & > ${BUTTON_SELECTOR} {
    align-self: flex-start;
    margin: 0 ${spacing.s16};
  }

  /* lower gap exceptions */
  & > ${BUTTON_SELECTOR}, & > ${BUTTON_GROUP_SELECTOR} {
    margin-top: -${spacing.s8};
  }

  & > ${SIDEBAR_MENU_MENU_SELECTOR} {
    margin: 0 ${spacing.s8};
  }

  & > ${TAB_LIST_SELECTOR} {
    margin: -${spacing.s16} 0;
    padding: ${spacing.s4};
  }

  ${SIDEBAR_MENU_MENU_ITEM_SELECTOR} {
    ${text2};
  }
`;

const SidebarMenuSectionWrapper = styled(Stack)`
  padding: ${spacing.s16} 0;
  border-bottom: 1px solid ${colors.borderSubtle};

  &:last-child {
    border-bottom: none;
  }

  & > ${SIDEBAR_MENU_MENU_SELECTOR} {
    color: red;
    margin: ${spacing.s16} ${spacing.s8} 0;
  }
`;

const SidebarMenuSectionIcons = styled.div`
  display: flex;
  align-items: center;
`;

export const SIDEBAR_MENU_SECTION_SELECTOR =
  SidebarMenuSectionWrapper.toString();

export type SidebarMenuSectionProps = {
  title?: string | React.ReactElement;
  onClick?: (isCollapsed: boolean) => void;
  children?: ReactNode;
  isCollapsible?: boolean;
  isCollapsed?: boolean;
  rightIconButton?: Pick<IconButtonProps, 'onClick' | 'iconName' | 'label'>;
  titleDataTestId?: string;
  infoPopover?: PopoverContentType;
  knowledgeBaseLink?: string;
  knowledgeHint?: PopoverContentType;
  gap?: SpaceVariant;
  itemsGap?: SpaceVariant;
  leftIcon?: {
    name?: IconName;
    size?: IconSize;
  };
  'aria-label'?: string;
  expandButtonLabel?: string;
  knowledgeBaseTooltip?: string;
};

export const SidebarMenuSection = ({
  isCollapsible = true,
  ...props
}: SidebarMenuSectionProps) => {
  if (isCollapsible) {
    return <CollapsableSidebarMenuSection {...props} />;
  }
  return <UncollapsableSidebarMenuSection {...props} />;
};

export const UncollapsableSidebarMenuSection = ({
  title,
  children,
  rightIconButton,
  titleDataTestId,
  infoPopover,
  knowledgeBaseLink,
  knowledgeHint,
  gap = 'medium',
  leftIcon,
  itemsGap = 'large',
  expandButtonLabel = 'Expand',
  knowledgeBaseTooltip = 'Knowledge base',
  ...props
}: SidebarMenuSectionProps) => {
  const leftIconName = leftIcon?.name;

  return (
    <SidebarMenuSectionWrapper
      gap={gap}
      role="region"
      aria-label={props['aria-label']}
    >
      {title && (
        <UncollapsableSidebarMenuSectionTitle data-test-id={titleDataTestId}>
          <FlexBox gap="small" align="center" width="full">
            {leftIconName && (
              <Icon
                iconName={leftIconName}
                iconSize={leftIcon?.size ?? IconSize.MEDIUM}
              />
            )}
            {typeof title === 'string' ? <span>{title}</span> : title}
            {knowledgeBaseLink && (
              <WithPopover content={knowledgeHint}>
                <Icon
                  iconName={IconName.HELP}
                  iconSize={IconSize.MEDIUM}
                  {...(!knowledgeHint
                    ? { 'data-tooltip-text': knowledgeBaseTooltip }
                    : {})}
                  deprecated_onClick={e => {
                    e.stopPropagation();
                    window.open(knowledgeBaseLink, '_blank noreferrer');
                  }}
                />
              </WithPopover>
            )}
            {infoPopover ? (
              <WithPopover content={infoPopover}>
                <Icon
                  iconName={IconName.INFO}
                  style={{ color: colors.grey500 }}
                />
              </WithPopover>
            ) : null}
          </FlexBox>
          <SidebarMenuSectionIcons>
            {rightIconButton && (
              <IconButton
                label={expandButtonLabel}
                onClick={rightIconButton.onClick}
                iconName={rightIconButton.iconName}
                buttonType={ButtonType.SECONDARY}
              />
            )}
          </SidebarMenuSectionIcons>
        </UncollapsableSidebarMenuSectionTitle>
      )}

      <SidebarMenuSectionBody gap={itemsGap}>{children}</SidebarMenuSectionBody>
    </SidebarMenuSectionWrapper>
  );
};

export const CollapsableSidebarMenuSection = ({
  title,
  onClick,
  children,
  rightIconButton,
  titleDataTestId,
  infoPopover,
  knowledgeBaseLink,
  knowledgeHint,
  gap = 'medium',
  leftIcon,
  itemsGap = 'large',
  knowledgeBaseTooltip = 'Knowledge base',
  ...props
}: SidebarMenuSectionProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    props.isCollapsed || false
  );
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const leftIconName = leftIcon?.name;

  return (
    <SidebarMenuSectionWrapper
      gap={gap}
      role="region"
      aria-label={props['aria-label']}
    >
      {title && (
        <CollapsableSidebarMenuSectionTitle
          // Setting type="button" to prevent the browser from triggering an onClick on this button
          // in case of form submission (e.g., pressing Enter within the form)
          type="button"
          data-test-id={titleDataTestId}
          onClick={() => {
            toggleCollapse?.();
            onClick?.(!isCollapsed);
          }}
        >
          <FlexBox gap="small" align="center" width="full">
            {leftIconName && (
              <Icon
                iconName={leftIconName}
                iconSize={leftIcon?.size ?? IconSize.MEDIUM}
              />
            )}
            {typeof title === 'string' ? <span>{title}</span> : title}
            {knowledgeBaseLink && (
              <WithPopover content={knowledgeHint}>
                <Icon
                  iconName={IconName.HELP}
                  iconSize={IconSize.MEDIUM}
                  {...(!knowledgeHint
                    ? { 'data-tooltip-text': knowledgeBaseTooltip }
                    : {})}
                  deprecated_onClick={e => {
                    e.stopPropagation();
                    window.open(knowledgeBaseLink, '_blank noreferrer');
                  }}
                />
              </WithPopover>
            )}
            {infoPopover ? (
              <WithPopover content={infoPopover}>
                <Icon
                  iconName={IconName.INFO}
                  style={{ color: colors.grey500 }}
                />
              </WithPopover>
            ) : null}
          </FlexBox>
          <SidebarMenuSectionIcons>
            {rightIconButton && (
              <IconButton
                label={rightIconButton.label}
                onClick={rightIconButton.onClick}
                iconName={rightIconButton.iconName}
                buttonType={ButtonType.GHOST}
              />
            )}
            <AnimatedChevron $isUp={!isCollapsed} />
          </SidebarMenuSectionIcons>
        </CollapsableSidebarMenuSectionTitle>
      )}
      {!isCollapsed && (
        <SidebarMenuSectionBody gap={itemsGap}>
          {children}
        </SidebarMenuSectionBody>
      )}
    </SidebarMenuSectionWrapper>
  );
};
